import { Check, Laptop, Monitor, Moon, Palette, Sun, SunMoon, Type } from "lucide-react";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "components/common/dropdown/DropDownV2";
import { SolarCycleDayPanel } from "./SolarCycleDayPanel";
import {
  APPEARANCE_FONT_OPTIONS,
  APPEARANCE_FONT_SIZE_OPTIONS,
  APPEARANCE_THEME_OPTIONS,
  DEFAULT_APPEARANCE,
  useAppearance,
  type AppearanceFont,
  type AppearanceFontSize,
  type AppearanceMode,
  type AppearanceSource,
  type AppearanceTheme,
  type ThemeCategory,
} from "providers/appearance";
import { cn } from "utils/cn";

const MODE_OPTIONS: Array<{
  value: AppearanceMode;
  label: string;
  icon: typeof Sun;
}> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

const SOURCE_OPTIONS: Array<{
  value: AppearanceSource;
  label: string;
  description: string;
  icon: typeof Sun;
}> = [
  {
    value: "manual",
    label: "Manual",
    description: "Choose your own color mode and preset.",
    icon: Palette,
  },
  {
    value: "solar",
    label: "Solar Cycle",
    description: "Match palettes to your local time of day.",
    icon: SunMoon,
  },
];

const THEME_CATEGORIES: Array<{ id: ThemeCategory; label: string }> = [
  { id: "cinematic", label: "On Set" },
  { id: "single-color", label: "Single Wash" },
  { id: "updated-classics", label: "Classic Stock" },
];

export const AppearanceThemeMenu = () => {
  const {
    source,
    mode,
    theme,
    font,
    fontSize,
    resolvedMode,
    solarPhase,
    solarBlend,
    setSource,
    setMode,
    setTheme,
    setFont,
    setFontSize,
  } = useAppearance();
  const isSolar = source === "solar";
  const previewMode = resolvedMode;

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        <Palette className="h-4 w-4 shrink-0" aria-hidden />
        <span className="flex-1">Appearance</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        collisionPadding={12}
        className="flex max-h-[min(80vh,42rem)] w-[28rem] flex-col gap-5 overflow-y-auto p-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Type className="h-3.5 w-3.5" aria-hidden />
              Font
            </span>
            <select
              value={font}
              onChange={(event) => setFont(event.target.value as AppearanceFont)}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              {APPEARANCE_FONT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id} className={option.previewClassName}>
                  {option.id === DEFAULT_APPEARANCE.font
                    ? `${option.label} (Default)`
                    : option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Font size</span>
            <select
              value={fontSize}
              onChange={(event) => setFontSize(event.target.value as AppearanceFontSize)}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              {APPEARANCE_FONT_SIZE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-muted-foreground">Theme source</span>
          <div className="grid grid-cols-2 gap-2">
            {SOURCE_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isActive = source === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setSource(option.value)}
                  className={cn(
                    "flex flex-col items-start gap-1.5 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive
                      ? "border-primary/50 bg-primary/10 text-foreground"
                      : "border-border bg-card text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  )}
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium">
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {option.label}
                  </span>
                  <span className="text-[11px] leading-snug text-muted-foreground/90">
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
          {isSolar && solarPhase ? (
            <SolarCycleDayPanel phase={solarPhase} resolvedMode={resolvedMode} blend={solarBlend} />
          ) : null}
        </div>

        {!isSolar ? (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Color mode</span>
              <div className="grid grid-cols-3 gap-1.5">
                {MODE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isActive = mode === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setMode(option.value)}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isActive
                          ? "border-primary/50 bg-primary/10 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {mode === "system" ? (
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Laptop className="h-3 w-3" aria-hidden />
                  Using {resolvedMode} mode
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">Theme preset</span>
                <p className="text-[11px] leading-snug text-muted-foreground/80">
                  Each preset includes a purpose-designed light and dark interface.
                </p>
              </div>

              {THEME_CATEGORIES.map((category) => {
                const options = APPEARANCE_THEME_OPTIONS.filter(
                  (option) => option.category === category.id
                );

                return (
                  <section key={category.id} className="flex flex-col gap-2">
                    <h3 className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                      {category.label}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {options.map((option) => {
                        const isActive = theme === option.id;
                        const preview = option.preview[previewMode];

                        return (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setTheme(option.id as AppearanceTheme)}
                            className={cn(
                              "relative flex min-w-0 flex-col gap-2 rounded-lg border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              isActive
                                ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                                : "border-border bg-card hover:bg-muted/50"
                            )}
                          >
                            <span
                              className="grid h-10 w-full grid-cols-[32%_1fr] overflow-hidden rounded-md border border-black/10"
                              aria-hidden
                            >
                              <span style={{ backgroundColor: preview.chrome }} />
                              <span
                                className="relative p-1.5"
                                style={{ backgroundColor: preview.canvas }}
                              >
                                <span
                                  className="absolute inset-x-1.5 top-1.5 h-4 rounded-sm"
                                  style={{ backgroundColor: preview.card }}
                                />
                                <span
                                  className="absolute bottom-1.5 left-1.5 h-2 w-1/2 rounded-sm"
                                  style={{ backgroundColor: preview.selected }}
                                />
                              </span>
                            </span>
                            <span className="flex items-center gap-1.5">
                              <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                                {option.label}
                              </span>
                              {isActive ? (
                                <Check
                                  className="h-3.5 w-3.5 shrink-0 text-primary"
                                  aria-label="Selected"
                                />
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        ) : null}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
